import { useId } from 'react';

const PAD = 3;

/**
 * Tiny trend line (pure SVG, no axes): the shape of the last days at a glance. All zeros draw a calm flat line
 * on the baseline. `label` is the accessible summary (screen readers get no picture); without one the line is
 * decorative (aria-hidden), for when the surrounding control already says what matters.
 */
export function Sparkline({ values, label, width = 96, height = 28, className = '' }: { values: number[]; label?: string; width?: number; height?: number; className?: string }) {
  const gradient = useId();
  if (values.length < 2) return null;
  const max = Math.max(...values);
  const step = (width - PAD * 2) / (values.length - 1);
  const y = (v: number) => (max > 0 ? height - PAD - (v / max) * (height - PAD * 2) : height - PAD);
  const pts = values.map((v, i) => [PAD + i * step, y(v)] as const);
  const line = pts.map(([x, py], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${py.toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)} ${height} L${PAD} ${height} Z`;
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg className={`spark ${max === 0 ? 'flat' : ''} ${className}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} focusable="false" {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}>
      <defs>
        <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {max > 0 && <path className="spark-area" d={area} fill={`url(#${gradient})`} />}
      <path className="spark-line" d={line} pathLength={1} fill="none" stroke="currentColor" strokeWidth={max > 0 ? 2 : 1.5} strokeLinecap="round" strokeLinejoin="round" />
      {max > 0 && <circle className="spark-dot" cx={lx} cy={ly} r={2.75} fill="currentColor" />}
    </svg>
  );
}
